import { client } from '../utils/shopify'

/**
 * Shopify API service using shopify-buy client
 * This replaces gatsby-source-shopify plugin functionality
 */

/**
 * Fetch all products from Shopify
 * @returns {Promise<Array>} Array of products
 */
export const getAllProducts = async () => {
    try {
        // Use shopify-buy client to fetch all products
        const products = await client.product.fetchAll()
        return products.map(transformProduct)
    } catch (error) {
        console.error('Error fetching all products:', error)
        throw error
    }
}

/**
 * Fetch a single product by handle
 * @param {string} handle - Product handle
 * @returns {Promise<Object>} Product object
 */
export const getProductByHandle = async (handle) => {
    try {
        const product = await client.product.fetchByHandle(handle)
        if (!product) {
            throw new Error(`Product with handle "${handle}" not found`)
        }
        return transformProduct(product)
    } catch (error) {
        console.error(`Error fetching product with handle "${handle}":`, error)
        throw error
    }
}

/**
 * Fetch all products from a specific collection
 * @param {string} collectionHandle - Collection handle (e.g., 'all')
 * @returns {Promise<Array>} Array of products in the collection
 */
export const getProductsFromCollection = async (collectionHandle) => {
    try {
        // First fetch all collections to find the one we want
        const collections = await client.collection.fetchAllWithProducts()
        const collection = collections.find(c => c.handle === collectionHandle)

        if (!collection) {
            throw new Error(`Collection with handle "${collectionHandle}" not found`)
        }

        return collection.products.map(transformProduct)
    } catch (error) {
        console.error(`Error fetching products from collection "${collectionHandle}":`, error)
        throw error
    }
}

/**
 * Fetch a collection by handle
 * @param {string} handle - Collection handle
 * @returns {Promise<Object>} Collection object with products
 */
export const getCollectionByHandle = async (handle) => {
    try {
        const collection = await client.collection.fetchByHandle(handle)
        if (!collection) {
            throw new Error(`Collection with handle "${handle}" not found`)
        }

        return {
            ...collection,
            products: collection.products.map(transformProduct)
        }
    } catch (error) {
        console.error(`Error fetching collection with handle "${handle}":`, error)
        throw error
    }
}

/**
 * Transform shopify-buy product to match gatsby-source-shopify structure
 * This ensures compatibility with existing components
 * @param {Object} product - Raw shopify-buy product
 * @returns {Object} Transformed product
 */
const transformProduct = (product) => {
    return {
        id: product.id,
        shopifyId: product.id,
        storefrontId: product.id,
        handle: product.handle,
        title: product.title,
        description: product.description,
        descriptionHtml: product.descriptionHtml,
        productType: product.productType,
        vendor: product.vendor,
        tags: product.tags,
        createdAt: product.createdAt,
        updatedAt: product.updatedAt,

        // Featured image
        featuredImage: product.images?.[0] ? {
            originalSrc: product.images[0].src,
            altText: product.images[0].altText || ''
        } : null,

        // All images
        images: product.images ? product.images.map(image => ({
            originalSrc: image.src,
            altText: image.altText || ''
        })) : [],

        // Options
        options: product.options ? product.options.map(option => ({
            id: option.id,
            name: option.name,
            values: option.values.map(value => value.value),
            position: option.position
        })) : [],

        // Variants
        variants: product.variants ? product.variants.map(variant => ({
            id: variant.id,
            shopifyId: variant.id,
            storefrontId: variant.id,
            title: variant.title,
            price: variant.price.amount,
            compareAtPrice: variant.compareAtPrice?.amount || null,
            availableForSale: variant.available,
            weight: variant.weight,
            weightUnit: variant.weightUnit,

            // Variant image
            image: variant.image ? {
                originalSrc: variant.image.src,
                altText: variant.image.altText || ''
            } : null,

            // Selected options
            selectedOptions: variant.selectedOptions ? variant.selectedOptions.map(option => ({
                name: option.name,
                value: option.value
            })) : []
        })) : [],

        // Price range
        priceRangeV2: {
            minVariantPrice: {
                amount: product.variants && product.variants.length > 0
                    ? Math.min(...product.variants.map(v => parseFloat(v.price.amount))).toFixed(2)
                    : '0.00'
            },
            maxVariantPrice: {
                amount: product.variants && product.variants.length > 0
                    ? Math.max(...product.variants.map(v => parseFloat(v.price.amount))).toFixed(2)
                    : '0.00'
            }
        }
    }
}

/**
 * Transform collection to match expected structure
 * @param {Object} collection - Raw shopify-buy collection
 * @returns {Object} Transformed collection
 */
const transformCollection = (collection) => {
    return {
        id: collection.id,
        handle: collection.handle,
        title: collection.title,
        description: collection.description,
        descriptionHtml: collection.descriptionHtml,
        image: collection.image ? {
            originalSrc: collection.image.src,
            altText: collection.image.altText || ''
        } : null,
        products: collection.products ? collection.products.map(transformProduct) : []
    }
}

export default {
    getAllProducts,
    getProductByHandle,
    getProductsFromCollection,
    getCollectionByHandle
} 